/* PDP redesign behaviour: gallery thumbnail rail sync + desktop tab switching */
(function () {
  'use strict';

  // Returns an update fn that flags which edges of a scroll container still have
  // hidden content; CSS turns the flags into a mask gradient. Pure CSS can't
  // detect scrollability, so this is the only way to show a fade *only* where
  // something is actually hidden. Used by the thumbnail rail.
  function createEdgeFade(el) {
    return function () {
      var scrollable = el.scrollHeight > el.clientHeight + 1;
      el.classList.toggle('is-scrollable', scrollable);
      el.classList.toggle('at-top', el.scrollTop <= 1);
      el.classList.toggle('at-bottom', el.scrollTop + el.clientHeight >= el.scrollHeight - 1);
    };
  }

  function initGalleryRail(scope) {
    var rail = scope.querySelector('[data-thumb-rail]');
    var main = scope.querySelector('[data-flickity-product-slider]');
    if (!rail || !main || typeof Flickity === 'undefined') return;

    var thumbs = Array.prototype.slice.call(rail.querySelectorAll('.yv-pdp-thumb'));
    if (!thumbs.length) return;

    var desktop = window.matchMedia('(min-width: 768px)');

    // The rail height is capped in CSS (absolutely positioned to the main image).
    // JS only toggles the fade classes so the edge gradient shows where content is
    // hidden, and scrolls the active thumb into view.
    var updateFade = createEdgeFade(rail);

    function setActive(index) {
      thumbs.forEach(function (t, i) {
        t.classList.toggle('is-active', i === index);
        if (i === index) {
          t.setAttribute('aria-current', 'true');
        } else {
          t.removeAttribute('aria-current');
        }
      });
      // Bring an off-screen active thumb (e.g. selected via the main arrows) into view by moving
      // the RAIL, never the page. Not scrollIntoView: even with block:'nearest' it scrolls the
      // document whenever part of the rail is below the fold, which it is on a short viewport.
      // The rail is the thumbs' offsetParent (it is positioned), so offsetTop is measured from
      // the rail's own top edge.
      if (desktop.matches && thumbs[index] && rail.scrollHeight > rail.clientHeight + 1) {
        var thumb = thumbs[index];
        var top = thumb.offsetTop;
        var bottom = top + thumb.offsetHeight;
        if (top < rail.scrollTop) {
          rail.scrollTop = top;
        } else if (bottom > rail.scrollTop + rail.clientHeight) {
          rail.scrollTop = bottom - rail.clientHeight;
        }
      }
    }

    // Flickity aria-hides the unselected cells, but with fade:true they stay in place and
    // their zoom links stay in the tab order — keyboard users land on images they can't see.
    function inertHiddenSlides(flkty) {
      flkty.cells.forEach(function (cell, i) {
        cell.element.inert = i !== flkty.selectedIndex;
      });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        var flkty = Flickity.data(main);
        if (flkty) {
          flkty.select(i);
        }
        setActive(i);
      });
    });

    rail.addEventListener('scroll', updateFade);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateFade, 150);
    });

    // Bind to the main slider once Flickity has initialised (product.js inits it async)
    var tries = 0;
    (function bind() {
      var flkty = Flickity.data(main);
      if (flkty) {
        setActive(flkty.selectedIndex || 0);
        inertHiddenSlides(flkty);
        updateFade();
        flkty.on('change', function (index) {
          setActive(index);
          inertHiddenSlides(flkty);
        });
        return;
      }
      if (tries++ < 40) setTimeout(bind, 150);
    })();

    // Init the fade state even if Flickity never initialises.
    updateFade();
  }

  // The gallery column is sticky on desktop (pdp-redesign.css, section 1). On a short laptop the
  // square image is taller than the room below the header, and a sticky element taller than its
  // room pins its top and hides its bottom — the image's lower half and the end of the thumbnail
  // rail — for the whole scroll. Measure rather than cap the image: when the wrapper does not fit,
  // flag it and hand the CSS a negative top, so it pins by its BOTTOM edge instead and still rides
  // down beside a taller buy box.
  function initGallerySticky(scope) {
    var wrapper = scope.querySelector('.product-detail-slider.main-slider-wrapper');
    if (!wrapper) return;
    var desktop = window.matchMedia('(min-width: 768px)');

    function measure() {
      if (!desktop.matches) {
        wrapper.classList.remove('pdp-gallery--overflows');
        wrapper.style.removeProperty('--pdp-gallery-top');
        return;
      }
      // --header-height is written onto <body> by sections/header.liquid; the fallback matches the
      // CSS's own, and the 20px is the sticky offset below the header.
      var header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 90;
      var room = window.innerHeight - header - 20;
      var overflows = wrapper.offsetHeight > room;
      wrapper.classList.toggle('pdp-gallery--overflows', overflows);
      // The same 20px gap below the gallery that the header offset leaves above it.
      if (overflows) wrapper.style.setProperty('--pdp-gallery-top', (window.innerHeight - wrapper.offsetHeight - 20) + 'px');
      else wrapper.style.removeProperty('--pdp-gallery-top');
    }

    measure();
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(measure, 150);
    });
    // The main image is lazy-loaded and Flickity sets the viewport height once it initialises, so
    // the wrapper's height settles after first paint; re-measure when it does. Toggling `position`
    // does not change the wrapper's height, so the observer cannot feed itself.
    window.addEventListener('load', measure);
    if (window.ResizeObserver) {
      new ResizeObserver(measure).observe(wrapper);
    }
  }

  function initMobilePagination(scope) {
    var slider = scope.querySelector('.data-main-slider');
    if (!slider) return;
    var dotsWrap = scope.querySelector('[data-pdp-dots]');
    var counter = scope.querySelector('[data-pdp-counter]');
    var dots = dotsWrap
      ? Array.prototype.slice.call(dotsWrap.querySelectorAll('.yv-pdp-dot'))
      : [];
    var current = counter ? counter.querySelector('[data-current]') : null;
    var count = dots.length || (current ? parseInt((counter.textContent.split('/')[1] || '0'), 10) : 0);
    if (count < 2) return;

    var raf = null;
    function update() {
      raf = null;
      // Only meaningful while the slider is a native scroll carousel (mobile / not Flickity)
      if (slider.classList.contains('flickity-enabled')) return;
      var w = slider.clientWidth || 1;
      var index = Math.round(slider.scrollLeft / w);
      if (index < 0) index = 0;
      if (index > count - 1) index = count - 1;
      if (dots.length) {
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === index); });
      }
      if (current) current.textContent = index + 1;
    }
    slider.addEventListener('scroll', function () {
      if (raf === null) raf = window.requestAnimationFrame(update);
    }, { passive: true });
  }

  function initTabs(scope) {
    var wrap = scope.querySelector('.yv-product-features');
    if (!wrap) return;
    var links = Array.prototype.slice.call(wrap.querySelectorAll('.feature-link'));
    var panels = Array.prototype.slice.call(
      wrap.querySelectorAll('.yv-product-feature-content .yv-product-feature')
    );
    if (!links.length || !panels.length) return;

    function activate(targetId) {
      links.forEach(function (link) {
        var isMatch = link.getAttribute('href') === '#' + targetId;
        // Keep both classes in sync: `is-active` drives the redesign underline,
        // `active` is the class the rest of the theme expects on the tab item.
        link.parentNode.classList.toggle('is-active', isMatch);
        link.parentNode.classList.toggle('active', isMatch);
      });
      panels.forEach(function (panel) {
        panel.classList.toggle('is-active', panel.id === targetId);
      });
    }

    links.forEach(function (link) {
      link.addEventListener('click', function (e) {
        // Stop the theme's delegated scroll-to handler (bound on <body>)
        e.preventDefault();
        e.stopPropagation();
        var target = (link.getAttribute('href') || '').replace('#', '');
        if (target) activate(target);
      });
    });

    // Default to the first tab/panel
    activate(panels[0].id);
  }

  // Size dropdowns. Rug variants and linked sizes share the component (.size-select). The rug
  // one wraps the real radio list, so its closed trigger has to follow whichever radio is checked
  // — by a click in the panel, an arrow key, or the sticky bar (product.js clicks the radio for
  // it). Linked sizes are plain links and only need the open/close behaviour.
  function initSizeSelect(scope) {
    var selects = Array.prototype.slice.call(scope.querySelectorAll('.size-select'));
    if (!selects.length) return;

    selects.forEach(function (select) {
      var trigger = select.querySelector('.size-select__trigger');
      var list = select.querySelector('.size-select__list');
      var current = select.querySelector('[data-size-select-current]');
      if (!trigger || !list) return;

      function sync() {
        var input = list.querySelector('input.productOption:checked');
        var row = input && input.nextElementSibling;
        if (!row) return;
        current.innerHTML = row.innerHTML;
        current.classList.toggle('is-unavailable', input.classList.contains('not-available'));
      }

      // Bring the chosen row into view inside the panel only. scrollIntoView would also scroll
      // the document whenever the panel runs below the fold.
      function revealSelected() {
        var row = list.querySelector('input:checked + .option, .option[aria-current="page"]');
        if (!row) return;
        var listBox = list.getBoundingClientRect();
        var rowBox = row.getBoundingClientRect();
        list.scrollTop += rowBox.top - listBox.top - (listBox.height - rowBox.height) / 2;
      }

      select.addEventListener('toggle', function () {
        if (select.open) revealSelected();
      });

      select.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && select.open) {
          select.open = false;
          trigger.focus();
        }
      });

      if (!current) return;

      // Deferred: common.js marks sold-out combinations in its own change handler, and the
      // trigger should strike the size if the shopper landed on one.
      list.addEventListener('change', function () {
        setTimeout(sync, 0);
      });

      // Close on the pick, never on change: arrow keys fire change on every radio they pass, so
      // closing there would shut the panel on the first keypress. A real pointer click carries
      // detail >= 1; the sticky bar's synthetic click carries 0 (and the panel is shut anyway).
      // Sync on every click too: product.js sets `checked` BEFORE it clicks the radio, so that
      // path never fires change. Capture phase, because jQuery's own click handling on the
      // radio stops that synthetic click before it can bubble back up to the list.
      list.addEventListener('click', function (e) {
        setTimeout(sync, 0);
        if (e.detail >= 1 && e.target.closest('.variant_option')) select.open = false;
      }, true);
    });

    document.addEventListener('click', function (e) {
      selects.forEach(function (select) {
        if (select.open && !select.contains(e.target)) select.open = false;
      });
    });
  }

  // The sticky bar's variant selects are only visible on desktop. product.js
  // syncs them one way (bar -> buy box); without the reverse the bar would still
  // show the initial size after the shopper picked another one in the buy box,
  // which reads as the wrong variant being about to be added.
  function initStickyBarOptions(scope) {
    var bar = scope.querySelector('#yvProductStickyBar');
    if (!bar) return;
    var selects = Array.prototype.slice.call(
      bar.querySelectorAll('.sticky-bar-product-options')
    );
    if (!selects.length) return;

    function sync() {
      selects.forEach(function (select) {
        var name = select.getAttribute('data-name');
        // Options render as radios (sizes, swatches) or as a select on long lists.
        var source =
          scope.querySelector('.productOption[name="' + name + '"]:checked') ||
          scope.querySelector('select.productOption[name="' + name + '"]');
        if (source && select.value !== source.value) select.value = source.value;
      });
    }

    // Delegated: common.js re-renders option rows when it hides unavailable
    // combinations, so per-input listeners wouldn't survive.
    scope.addEventListener('change', function (e) {
      if (e.target && e.target.classList.contains('productOption')) sync();
    });
    sync();
  }

  // The sale pill is rendered server-side from the product-level discount test, which
  // is right for the product but not necessarily for the variant the shopper lands on.
  // priceUpdate() in common.js rewrites the innerHTML of [data-price-wrapper] on every
  // variant change and only emits .yv-product-compare-price when that variant is
  // actually reduced — so watching that subtree tells us the variant's sale state
  // without reaching into common.js internals or adding a new event.
  function initSaleBanner(scope) {
    var banner = scope.querySelector('[data-sale-banner][data-requires-discount="true"]');
    if (!banner || typeof MutationObserver === 'undefined') return;

    // [data-price-wrapper] is NOT unique inside the section — product-grid.liquid puts one
    // on every card, so the similar-products and complementary-products rails have their
    // own. Go via .yv-pricebox-outer, which only the main buy box has.
    var buyBox = banner.closest('.yv-main-product-detail') || scope;
    var priceWrapper = buyBox.querySelector('.yv-pricebox-outer [data-price-wrapper]');
    if (!priceWrapper) return;

    function sync() {
      banner.hidden = !priceWrapper.querySelector('.yv-product-compare-price');
    }

    new MutationObserver(sync).observe(priceWrapper, { childList: true, subtree: true });
    sync();
  }

  // Estimated-delivery formatting lives in delivery-estimate.js so the cart
  // renders identical dates. That file is loaded before this one.

  // Finance hook + drawer (.pdp-finance / .finance-modal). Which providers exist at
  // all is settled server-side from shop.enabled_payment_types — see
  // snippets/product-finance-options.liquid. What is left for the client is the part
  // that depends on the price: this store prices per variant (rug sizes, sofa configs),
  // so every instalment figure, and which providers will take the order at all, changes
  // as the shopper switches variant.
  //
  // The registry rides in the same JSON blob as the prices rather than being restated
  // here, so the Liquid and the JS cannot disagree about a provider's spend limits.
  function initFinance(root) {
    var configEl = root.querySelector('[data-finance-config]');
    if (!configEl) return;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return; // leave the server-rendered figures in place
    }

    var providers = (config && config.providers) || [];
    var prices = (config && config.prices) || {};
    if (!providers.length) return;

    // The drawer is a sibling of the hook inside .pdp-finance-block, not a descendant
    // of it — a <dialog> opened with showModal() is promoted to the top layer, but it
    // stays where it was rendered in the DOM.
    //
    // .yv-pricebox-outer is still accepted: the hook lived under the price before it moved
    // beneath the add-to-cart button, and sections/featured-product.liquid could put one back
    // there. The `document` fallback is the last resort, not the normal path — it would find
    // another buy box's drawer on any page that rendered two.
    var scope = root.closest('.pdp-finance-block, .yv-pricebox-outer') || document;
    var drawer = scope.querySelector('[data-finance-drawer]');
    var options = scope.querySelectorAll('[data-finance-option]');
    var ctaName = scope.querySelector('[data-finance-cta-name]');
    var noneMsg = scope.querySelector('[data-finance-none]');

    // Shopify.formatMoney and moneyFormat are both set up by common.js /
    // theme-scripts.liquid, which load before this file. If either is missing, every
    // amount keeps whatever Liquid rendered rather than showing a bare number.
    var canFormat =
      window.Shopify &&
      typeof window.Shopify.formatMoney === 'function' &&
      typeof moneyFormat !== 'undefined';

    function money(cents) {
      return canFormat ? window.Shopify.formatMoney(cents, moneyFormat) : null;
    }

    function setMoney(el, cents) {
      if (!el) return;
      var text = money(cents);
      if (text !== null) el.textContent = text;
    }

    function providerFor(id) {
      for (var i = 0; i < providers.length; i++) {
        if (providers[i].id === id) return providers[i];
      }
      return null;
    }

    function update(price) {
      if (typeof price !== 'number') return;

      var best = null;
      var eligible = 0;

      Array.prototype.forEach.call(options, function (option) {
        var provider = providerFor(option.getAttribute('data-finance-id'));
        if (!provider) return;

        var ok = price >= provider.min && price <= provider.max;
        option.hidden = !ok;
        if (!ok) return;
        eligible++;

        // Integer division, then the first payment absorbs the remainder — the same
        // split the Liquid does, and the same one every one of these providers uses.
        var each = Math.floor(price / provider.count);
        var first = price - each * (provider.count - 1);

        var steps = option.querySelectorAll('[data-finance-step]');
        Array.prototype.forEach.call(steps, function (step, i) {
          setMoney(step, i === 0 ? first : each);
        });
        setMoney(option.querySelector('[data-finance-each]'), each);

        if (best === null || each < best.each) best = { each: each, provider: provider };
      });

      // Everything in the drawer, and the line that opens it, disappear together when
      // no provider will take this variant. The blob stays in the DOM, so another
      // variant brings the whole treatment back.
      root.hidden = eligible === 0;
      if (noneMsg) noneMsg.hidden = eligible > 0;

      if (best) {
        Array.prototype.forEach.call(scope.querySelectorAll('[data-finance-from]'), function (el) {
          setMoney(el, best.each);
        });
        Array.prototype.forEach.call(scope.querySelectorAll('[data-finance-cadence-per]'), function (el) {
          el.textContent = best.provider.perNoun;
        });
      }

      Array.prototype.forEach.call(scope.querySelectorAll('[data-finance-total]'), function (el) {
        setMoney(el, price);
      });

      // The selected provider may have just been ruled out. Moving the selection to the
      // first row still standing keeps a schedule on screen and the footer button named.
      if (drawer) {
        var checked = drawer.querySelector('.finance-option__radio:checked');
        if (!checked || checked.closest('[data-finance-option]').hidden) {
          for (var i = 0; i < options.length; i++) {
            if (!options[i].hidden) {
              options[i].querySelector('.finance-option__radio').checked = true;
              break;
            }
          }
        }
        syncCta();
      }
    }

    // The schedule's dates. Liquid renders "In 1 month" / "In 2 months", because the page is
    // cached and a calendar date baked in at render time would be the day the cache filled. Here
    // the day is the shopper's own, so the rows can read "23 November" as the design has them.
    // Price-independent, so this runs once rather than inside update().
    //
    // Calendar months for the monthly plans. Klarna strictly bills every 30 days, so its later
    // dates can be a day out — close enough for a preview the provider confirms at checkout.
    function dateSchedule() {
      if (typeof Date.prototype.toLocaleDateString !== 'function') return;
      var today = new Date();

      Array.prototype.forEach.call(options, function (option) {
        var unit = option.getAttribute('data-finance-unit');
        var size = parseInt(option.getAttribute('data-finance-step-size'), 10) || 1;

        Array.prototype.forEach.call(option.querySelectorAll('[data-finance-when]'), function (el) {
          var n = parseInt(el.getAttribute('data-finance-when'), 10);
          if (!n) return; // the first row stays "Today"

          var d;
          if (unit === 'month') {
            // Clamp the day, or 31 January plus one month rolls over into March.
            var target = today.getMonth() + n * size;
            var last = new Date(today.getFullYear(), target + 1, 0).getDate();
            d = new Date(today.getFullYear(), target, Math.min(today.getDate(), last));
          } else if (unit === 'week') {
            d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + n * size * 7);
          } else {
            return; // an unknown cadence keeps its server-rendered label
          }

          // en-GB, not the document language: the shop's lang is a bare "en", which formats as
          // "November 23", and every provider in the registry is a UK plan.
          el.textContent = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
        });
      });
    }

    // Shopify has no way to preselect a gateway from the product page, so the footer
    // button adds to basket and goes to checkout. Naming it after the chosen provider
    // is what keeps it reading as the continuation of that choice.
    function syncCta() {
      if (!ctaName || !drawer) return;
      var checked = drawer.querySelector('.finance-option__radio:checked');
      var option = checked && checked.closest('[data-finance-option]');
      if (option) ctaName.textContent = option.getAttribute('data-finance-name');
    }

    if (drawer) {
      drawer.addEventListener('change', function (e) {
        if (e.target && e.target.classList.contains('finance-option__radio')) syncCta();
      });
    }

    dateSchedule();

    // ---- Footer CTA: add to basket, then straight to checkout ----
    //
    // The markup still declares this as a plain submit button owned by the buy-box form, with
    // `name="return_to" value="/checkout"` — that is the no-JS path and it stays. What follows
    // takes it over whenever fetch is available, because the native POST is doing its work three
    // layers away from the button: the form is in a different section block, the button reaches it
    // by `form=` from inside a <dialog> the browser has promoted to the top layer, and the theme's
    // cart drawer is meanwhile binding its own AJAX handler to a sibling button in that same form.
    // Driving it here makes the outcome the button's own: one request we can see the result of,
    // and a redirect that does not depend on how `return_to` is honoured.
    //
    // /cart/add.js rather than a full-page POST also means an error (the variant sold out while
    // the drawer was open, a quantity above what is left) comes back as JSON we can show on the
    // page, instead of dumping the shopper on Shopify's own error screen having lost the drawer.
    var cta = scope.querySelector('[data-finance-cta]');
    // .form resolves the `form=` attribute; the getElementById is the belt-and-braces path for the
    // case where that association is what is broken, which is exactly the failure being fixed.
    var ctaForm = cta && (cta.form || document.getElementById(cta.getAttribute('form') || ''));

    if (cta && ctaForm && typeof window.fetch === 'function' && typeof window.FormData === 'function') {
      // Both are declared as globals by snippets/theme-scripts.liquid, which loads first. Bare
      // identifiers, not window.* — they are `const`, so they live in the global lexical scope and
      // never become properties of window.
      var addUrl = typeof cartAddUrl !== 'undefined' ? cartAddUrl : '/cart/add.js';

      // Locale-aware: on a market with a path prefix, /checkout is a 404 and /en-gb/checkout is not.
      var checkoutUrl = function () {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return root.replace(/\/+$/, '') + '/checkout';
      };

      // The buy box's own error slot — the one the theme's add-to-cart already writes into, so a
      // failure from this button reads exactly like a failure from that one.
      var errorBox = function () {
        var owner = ctaForm.closest('.yv-main-product-detail') || document;
        return owner.querySelector('.productErrors');
      };

      var showError = function (message) {
        var box = errorBox();
        if (!box) {
          // Nowhere to put it is not a reason to swallow it.
          window.alert(message);
          return;
        }
        box.textContent = message;
        box.style.display = '';
        // The message is on the page behind the drawer; closing it is the only way it is seen.
        var dialog = cta.closest('dialog.modal');
        if (dialog && typeof dialog.close === 'function' && dialog.hasAttribute('open')) {
          dialog.close();
        }
        box.scrollIntoView({ block: 'nearest' });
      };

      cta.addEventListener('click', function (event) {
        event.preventDefault();
        if (cta.disabled) return;

        // Every field the buy box collects — the variant id, the quantity, the selling plan and
        // any line-item properties — comes along, exactly as it does for the theme's own
        // add-to-cart. new FormData(form) excludes the submitter's own name/value, which is what
        // we want: `return_to` is the no-JS path's business and means nothing to /cart/add.js.
        var body = new FormData(ctaForm);

        cta.disabled = true;
        cta.classList.add('is-loading');

        window
          .fetch(addUrl, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: body,
            credentials: 'same-origin'
          })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw data;
              return data;
            });
          })
          .then(function () {
            // Left disabled and loading on purpose: the navigation is already under way, and a
            // button that comes back to life underneath it invites a second add.
            window.location.href = checkoutUrl();
          })
          .catch(function (error) {
            cta.disabled = false;
            cta.classList.remove('is-loading');
            // Only Shopify's own `description` is shown. A thrown Error also has `.message`, and
            // putting "Unexpected token < in JSON" in front of a shopper is worse than saying
            // nothing useful at all.
            var described = error && typeof error.description === 'string' && error.description;
            showError(described || 'Sorry, we could not add this to your basket. Please try again.');
          });
      });
    }

    // common.js sets [name="id"].value on variant change and dispatches a bubbling
    // `change` — see the _productParent block in variantChange(). The hook sits outside
    // the form, so closest('form') finds nothing; go via the buy-box wrapper, which is
    // tight enough to exclude the add-to-cart forms on the product cards further down.
    var buyBox = root.closest('.yv-main-product-detail');
    var idInput = buyBox && buyBox.querySelector('form[action*="/cart/add"] [name="id"]');
    if (!idInput) return;

    idInput.addEventListener('change', function () {
      update(prices[idInput.value]);
    });
  }

  // Keeps the "Request a trade quote" link pointing at the product AND the variant on screen.
  //
  // The link renders as a plain /account#quote in snippets/product-form.liquid, which is the
  // working no-JS destination — the quote form, with nothing filled in. This adds
  // ?quote_item=<a line describing what is selected>, which initTradeQuote() in
  // assets/account-redesign.js reads, appends to the products box, and strips back out of the URL.
  //
  // The href is rebuilt on every variant and quantity change rather than on click. Building it in
  // a click handler would work for a plain left-click, but a middle-click fires auxclick and never
  // click, so opening the quote in a new tab would silently lose the product. Keeping the href
  // honest at all times means every way of following a link gets the same thing.
  //
  // Renders for trade customers only, so on almost every page load this returns immediately.
  function initTradeQuote(scope) {
    var link = scope.querySelector('[data-trade-quote-link]');
    if (!link) return;

    var base = (link.getAttribute('href') || '').split('#')[0];
    var form = document.getElementById(link.getAttribute('data-form-id'));
    if (!form) return;

    var idInput = form.querySelector('[name="id"]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var variants = [];

    // Hand-assembled in snippets/product-variants.liquid rather than piped through the `json`
    // filter, so a stray character in a variant title can break it. A broken blob costs the size
    // and the SKU, not the link.
    var blob = scope.querySelector('[data-name="variant-json"]');
    if (blob) {
      try {
        variants = JSON.parse(blob.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }

    function currentVariant() {
      var id = idInput ? String(idInput.value) : '';
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === id) return variants[i];
      }
      return null;
    }

    function buildItem() {
      var variant = currentVariant();
      var line = link.getAttribute('data-product-title') || '';

      // 'Default Title' is Shopify's placeholder on a product with no real options. Printing it
      // would put "— Default Title" in front of the trade team on every unvaried product.
      if (variant && variant.title && variant.title !== 'Default Title') {
        line += ' — ' + variant.title;
      }
      if (variant && variant.sku) {
        line += ' (SKU ' + variant.sku + ')';
      }

      var qty = parseInt(qtyInput && qtyInput.value, 10);
      line += ' × ' + (qty > 0 ? qty : 1);

      var url = link.getAttribute('data-product-url');
      if (url) {
        line += ' — ' + url + (variant ? '?variant=' + variant.id : '');
      }
      return line;
    }

    function refresh() {
      link.setAttribute('href', base + '?quote_item=' + encodeURIComponent(buildItem()) + '#quote');
    }

    // common.js sets [name="id"].value on variant change and dispatches a bubbling `change`.
    if (idInput) idInput.addEventListener('change', refresh);
    if (qtyInput) {
      qtyInput.addEventListener('change', refresh);
      // The stepper buttons write the value directly, and not every path through them fires
      // `change`; `input` catches typing and covers the rest.
      qtyInput.addEventListener('input', refresh);
    }

    refresh();
  }

  // Keeps the estimated-delivery badge honest when the shopper switches variant.
  //
  // The band (in stock / low / out) is the only part that varies: estimated_delivery_date,
  // supplier_restock_date and made_to_order are all product-level metafields, so the dates
  // themselves are the same whichever variant is chosen. That makes this a class-and-label
  // swap rather than a section re-render, and it means this function never touches the two
  // date lines — the formatted dates written by assets/delivery-estimate.js survive intact.
  // Which of those lines is on screen follows from the is-*-stock modifier in CSS.
  //
  // Not called on load: snippets/product-form.liquid renders the band for
  // selected_or_first_available_variant, which already honours ?variant= in the URL.
  // Plural, because there can legitimately be two: the `stock_indicator` block and the trust
  // row's own badge (snippets/product-form.liquid, `icons` block) both carry the contract, and a
  // merchant who switches the badges back on gets both. A singular querySelector left the second
  // one frozen on whatever variant the page was rendered for.
  function initDeliveryBand(scope) {
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-delivery-band]'),
      initDeliveryBandFor
    );
  }

  function initDeliveryBandFor(badge) {
    // Same route as initFinance above: the hook sits outside the form, and the buy-box
    // wrapper is tight enough to exclude the add-to-cart forms on the cards further down.
    var buyBox = badge.closest('.yv-main-product-detail');
    var idInput = buyBox && buyBox.querySelector('form[action*="/cart/add"] [name="id"]');
    var status = badge.querySelector('.yv-delivery-status');
    if (!idInput || !status) return;

    var label = badge.querySelector('.inventory-status-label');
    var threshold = parseInt(badge.getAttribute('data-low-threshold'), 10);
    if (isNaN(threshold)) threshold = 6;

    // Hand-assembled in snippets/product-variants.liquid rather than piped through the
    // `json` filter, so a stray character in a variant title can break it. A broken blob
    // leaves the server-rendered band alone, which is still right for the variant on
    // screen at load.
    var variants = [];
    var blob = buyBox.querySelector('[data-name="variant-json"]');
    if (blob) {
      try {
        variants = JSON.parse(blob.textContent) || [];
      } catch (e) {
        variants = [];
      }
    }
    if (!variants.length) return;

    var BANDS = ['is-in-stock', 'is-low-stock', 'is-out-of-stock'];
    var LABELS = ['in-stock', 'low-stock', 'no-stock'];
    var COPY = ['data-status-in', 'data-status-low', 'data-status-out'];

    // common.js sets [name="id"].value on variant change and dispatches a bubbling
    // `change`. Its handler is wrapped in a 500ms setTimeout and fires the event at the
    // end, so by the time this runs the price and add-to-cart label have already updated.
    idInput.addEventListener('change', function () {
      var id = String(idInput.value);
      var variant = null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === id) {
          variant = variants[i];
          break;
        }
      }
      if (!variant) return;

      // inventory_quantity is rendered as a quoted string ("12", "0", "-3").
      var qty = Number(variant.inventory_quantity);
      var band;
      if (!variant.inventory_management) {
        // Untracked variants report a quantity of 0 and are always purchasable. Reading
        // that as "Out of stock" would put the wrong message on something you can buy.
        band = 0;
      } else if (qty > threshold) {
        band = 0;
      } else if (qty > 0) {
        band = 1;
      } else {
        // <= 0 rather than == 0, so oversold variants land here too. A negative quantity
        // used to match no band at all and left the badge with no status text.
        band = 2;
      }

      for (var b = 0; b < BANDS.length; b++) {
        badge.classList.toggle(BANDS[b], b === band);
        if (label) label.classList.toggle(LABELS[b], b === band);
      }
      status.textContent = (badge.getAttribute(COPY[band]) || '').replace('{count}', qty);
    });
  }

  // The fabric picker for made-to-order upholstery (design 3b): a strip of six
  // swatches, a `+N` tile that opens the full drawer, and a readout naming the
  // chosen cloth and its range.
  //
  // Neither the strip chips nor the drawer tiles are radios. common.js resolves
  // the selected combination by walking every .product-loop-variants in the
  // section and taking its checked input, and marks availability by indexing into
  // .productOption positionally — a duplicate set of either would silently select
  // the wrong variant. So the one real list stays in the DOM (hidden), and every
  // chip and tile CLICKS the radio it stands for, exactly as assets/product.js
  // does for the sticky bar. This function then mirrors state back onto them.
  //
  // Both kinds of control carry [data-fabric-pick], so one delegated handler and
  // one sync pass drive the strip and the drawer alike.
  function initFabricSwatches(scope) {
    var strip = scope.querySelector('[data-fabric-strip]');
    var chipRow = strip && strip.querySelector('.fabric-strip__chips');
    if (!strip || !chipRow) return;

    var readoutName = scope.querySelector('[data-fabric-readout-name]');
    var readoutRange = scope.querySelector('[data-fabric-readout-range]');

    // The strip as it was rendered, before any reordering below. Restored on every
    // sync so the "pull the chosen cloth to the front" move is computed from the
    // original six each time rather than compounding across selections.
    var chipRowHTML = chipRow.innerHTML;

    function checkedInput() {
      return scope.querySelector('.fabric-swatches .productOption:checked');
    }

    function findRadio(value) {
      return Array.prototype.filter.call(
        scope.querySelectorAll('.fabric-swatches .productOption'),
        function (input) {
          return input.value === value;
        }
      )[0];
    }

    function syncReadout() {
      var input = checkedInput();
      if (!input) return;
      if (readoutName) {
        readoutName.textContent = input.getAttribute('data-fabric-label') || input.value;
      }
      if (readoutRange) {
        readoutRange.textContent = input.getAttribute('data-fabric-range') || '';
      }
    }

    // Build a strip chip for a value that was not among the rendered six. The
    // swatch is cloned from that value's own row in the hidden list — the row
    // already holds the resolved image, whichever of the snippet's three fallbacks
    // produced it, so there is one source of truth and no way for the strip to
    // disagree with the drawer.
    function chipFor(value) {
      var input = findRadio(value);
      var from = input && input.parentElement && input.parentElement.querySelector('.fabric-row__chip');
      if (!from) return null;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'fabric-strip__chip';
      button.setAttribute('data-fabric-pick', value);

      var swatch = document.createElement('span');
      swatch.className = 'fabric-strip__swatch';
      swatch.innerHTML = from.innerHTML;
      if (from.style.backgroundColor) swatch.style.backgroundColor = from.style.backgroundColor;

      var name = document.createElement('span');
      name.className = 'yv-visually-hidden';
      name.textContent = input.getAttribute('data-fabric-label') || value;

      button.appendChild(swatch);
      button.appendChild(name);
      return button;
    }

    // One pass over every [data-fabric-pick] in the section — strip chips and
    // drawer tiles both. Three jobs:
    //
    // 1. mirror common.js's availability marking. It writes .not-available on the
    //    radio and .hidden on its <li>; neither control can know that on its own.
    // 2. mark the chosen one with aria-pressed, which is also what CSS draws the
    //    3b selection outline from.
    // 3. make sure the chosen cloth is actually ON the strip. If it is not one of
    //    the six, the last chip is dropped and a chip for it goes to the front,
    //    which is where 3b draws the selection. The `+N` tile keeps its count: it
    //    was always "the rest", and it still is.
    function syncPicks() {
      var selected = checkedInput();
      var selectedValue = selected ? selected.value : null;

      // The rebuild below destroys the chip a keyboard user just pressed, which
      // drops focus to <body>. Note which chip had it so it can be handed back.
      var focused = document.activeElement;
      var focusedPick = focused && chipRow.contains(focused) && focused.closest
        ? focused.closest('[data-fabric-pick]')
        : null;
      var focusedValue = focusedPick ? focusedPick.getAttribute('data-fabric-pick') : null;

      chipRow.innerHTML = chipRowHTML;

      if (selectedValue && !chipRow.querySelector('[data-fabric-pick="' + cssEscape(selectedValue) + '"]')) {
        var chip = chipFor(selectedValue);
        if (chip) {
          var chips = chipRow.querySelectorAll('.fabric-strip__chip');
          if (chips.length) chipRow.removeChild(chips[chips.length - 1]);
          chipRow.insertBefore(chip, chipRow.firstChild);
        }
      }

      Array.prototype.forEach.call(
        scope.querySelectorAll('[data-fabric-pick]'),
        function (pick) {
          var value = pick.getAttribute('data-fabric-pick');
          var input = findRadio(value);
          pick.setAttribute('aria-pressed', value === selectedValue ? 'true' : 'false');
          if (!input) return;
          pick.classList.toggle('not-available', input.classList.contains('not-available'));
          if (pick.parentElement && pick.parentElement.classList.contains('fabric-drawer__item')) {
            pick.parentElement.classList.toggle(
              'hidden',
              !!(input.parentElement && input.parentElement.classList.contains('hidden'))
            );
          } else {
            pick.classList.toggle(
              'hidden',
              !!(input.parentElement && input.parentElement.classList.contains('hidden'))
            );
          }
        }
      );

      if (focusedValue) {
        var refocus = chipRow.querySelector('[data-fabric-pick="' + cssEscape(focusedValue) + '"]')
          || (selectedValue && chipRow.querySelector('[data-fabric-pick="' + cssEscape(selectedValue) + '"]'));
        if (refocus) refocus.focus();
      }
    }

    // Fabric names come from merchant-entered metaobjects and land in an attribute
    // selector, so they have to be escaped. CSS.escape is not in every browser this
    // theme still serves; the fallback quotes the characters that can terminate an
    // attribute selector, which is all this use needs.
    function cssEscape(value) {
      if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
      return value.replace(/["\\\]]/g, '\\$&');
    }

    // Delegated on the section, not on the strip or the drawer: common.js rewrites
    // option rows when it hides combinations that don't exist, and syncPicks()
    // itself replaces the strip's contents, so per-element listeners would not
    // survive either.
    scope.addEventListener('click', function (e) {
      var pick = e.target.closest ? e.target.closest('[data-fabric-pick]') : null;
      if (!pick || !scope.contains(pick)) return;

      var input = findRadio(pick.getAttribute('data-fabric-pick'));
      if (!input || input.classList.contains('not-available')) return;

      // .click() rather than setting .checked: common.js listens for the click, and
      // it is what recalculates price, availability and the gallery.
      input.click();

      // A pick made inside the drawer closes it; a pick on the strip has nothing
      // to close.
      var dialog = pick.closest('dialog');
      var close = dialog && dialog.querySelector('[data-modal-close]');
      if (close) close.click();
    });

    scope.addEventListener('change', function (e) {
      if (!e.target || !e.target.classList.contains('productOption')) return;
      syncReadout();
      syncPicks();
    });

    syncReadout();
    syncPicks();
  }

  function init() {
    var sections = document.querySelectorAll('.product-section');
    Array.prototype.forEach.call(sections, function (section) {
      initGalleryRail(section);
      initGallerySticky(section);
      initMobilePagination(section);
      initTabs(section);
      initSizeSelect(section);
      initStickyBarOptions(section);
      initFabricSwatches(section);
      initSaleBanner(section);
      initTradeQuote(section);
      if (window.YvDeliveryEstimate) {
        window.YvDeliveryEstimate.init(section);
        window.YvDeliveryEstimate.initRestock(section);
      }
      initDeliveryBand(section);
    });

    // Inside the loop would also work — the finance hook only renders for mainProduct,
    // which only main-product.liquid passes — but querying the document keeps it
    // findable if the buy box ever moves.
    Array.prototype.forEach.call(document.querySelectorAll('[data-finance]'), initFinance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
